'use client';

import { useEffect, useRef, useCallback, useState } from 'react';
import { mutate } from 'swr';
import { getBrowserClient } from '@/lib/integrations/supabase/client';
import { swrKeys } from './config';
import { useBookingsForDate, useUpcomingBookings, useDueBookings } from './hooks';
import type { BookingWithDetails } from '@/lib/integrations/supabase/types';
import type { RealtimePostgresChangesPayload, RealtimeChannel } from '@supabase/supabase-js';

const MAX_RECONNECT_ATTEMPTS = 5;
const BASE_RECONNECT_DELAY = 1000;

interface BookingChangePayload {
  id: string;
  business_id: string;
  customer_id: string | null;
  staff_id: string | null;
  start_at: string;
  end_at: string;
  status: string;
  notes: string | null;
  is_walk_in: boolean;
  paid: boolean;
  [key: string]: unknown;
}

export function useScheduleRealtime(
  businessId: string | null,
  date: Date | null,
  staffId?: string | null
) {
  const { data = [], isLoading, isValidating, mutate: mutateBookings } = useBookingsForDate(
    businessId,
    date,
    staffId
  );

  const channelRef = useRef<RealtimeChannel | null>(null);
  const dateStrRef = useRef<string | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef(0);
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected' | 'reconnecting'>('connecting');

  const dateStr = date
    ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
    : null;

  const handleBookingChange = useCallback(
    (payload: RealtimePostgresChangesPayload<BookingChangePayload>) => {
      if (!businessId || !dateStr) return;

      const eventType = payload.eventType;
      const newRecord = payload.new as BookingChangePayload | null;
      const oldRecord = payload.old as { id: string } | null;

      const isForCurrentDate = (booking: BookingChangePayload | null): boolean => {
        if (!booking?.start_at) return false;
        const bookingDate = new Date(booking.start_at);
        const bookingDateStr = `${bookingDate.getFullYear()}-${String(bookingDate.getMonth() + 1).padStart(2, '0')}-${String(bookingDate.getDate()).padStart(2, '0')}`;
        return bookingDateStr === dateStr;
      };

      const matchesStaffFilter = (booking: BookingChangePayload | null): boolean => {
        if (!staffId) return true;
        return booking?.staff_id === staffId;
      };

      switch (eventType) {
        case 'INSERT': {
          if (!newRecord || !isForCurrentDate(newRecord) || !matchesStaffFilter(newRecord)) {
            return;
          }

          mutateBookings(
            async (current) => {
              if (current?.some((b: BookingWithDetails) => b.id === newRecord.id)) {
                return current;
              }
              return undefined;
            },
            { revalidate: true }
          );
          break;
        }

        case 'UPDATE': {
          if (!newRecord) return;

          const wasForCurrentDate = oldRecord && isForCurrentDate(oldRecord as unknown as BookingChangePayload);
          const isNowForCurrentDate = isForCurrentDate(newRecord);

          if (wasForCurrentDate !== isNowForCurrentDate) {
            if (wasForCurrentDate && !isNowForCurrentDate) {
              mutateBookings(
                (current) => current?.filter((b: BookingWithDetails) => b.id !== newRecord.id),
                { revalidate: false }
              );
            }
            return;
          }

          if (!isNowForCurrentDate) return;

          if (staffId && newRecord.staff_id !== staffId) {
            mutateBookings(
              (current) => current?.filter((b: BookingWithDetails) => b.id !== newRecord.id),
              { revalidate: false }
            );
            return;
          }

          mutateBookings(
            (current) => {
              if (!current) return current;

              const existingIndex = current.findIndex((b: BookingWithDetails) => b.id === newRecord.id);
              if (existingIndex === -1) return current;

              const updated = [...current];
              updated[existingIndex] = {
                ...current[existingIndex],
                status: newRecord.status as BookingWithDetails['status'],
                start_at: newRecord.start_at,
                end_at: newRecord.end_at,
                notes: newRecord.notes,
                paid: newRecord.paid,
                is_walk_in: newRecord.is_walk_in,
                confirmation_status: (newRecord as any).confirmation_status,
              } as BookingWithDetails;

              return updated;
            },
            { revalidate: false }
          );
          break;
        }

        case 'DELETE': {
          const deletedId = oldRecord?.id;
          if (!deletedId) return;

          mutateBookings(
            (current) => current?.filter((b: BookingWithDetails) => b.id !== deletedId),
            { revalidate: false }
          );
          break;
        }
      }
    },
    [businessId, dateStr, staffId, mutateBookings]
  );

  const cleanup = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (channelRef.current) {
      const supabase = getBrowserClient();
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!businessId || !dateStr) {
      return;
    }

    if (dateStrRef.current !== dateStr && channelRef.current) {
      cleanup();
      reconnectAttempts.current = 0;
    }

    dateStrRef.current = dateStr;

    const supabase = getBrowserClient();
    const channelName = `schedule-${businessId}-${dateStr}`;

    const subscribe = () => {
      setConnectionStatus(reconnectAttempts.current > 0 ? 'reconnecting' : 'connecting');

      const channel = supabase
        .channel(channelName)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'bookings',
            filter: `business_id=eq.${businessId}`,
          },
          handleBookingChange
        )
        .subscribe((status) => {
          if (status === 'SUBSCRIBED') {
            setConnectionStatus('connected');
            reconnectAttempts.current = 0;
          } else if (status === 'CLOSED' || status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            setConnectionStatus('disconnected');

            if (reconnectAttempts.current < MAX_RECONNECT_ATTEMPTS) {
              const delay = BASE_RECONNECT_DELAY * Math.pow(2, reconnectAttempts.current);

              reconnectTimeoutRef.current = setTimeout(() => {
                reconnectAttempts.current++;
                cleanup();
                subscribe();
              }, delay);
            }
          }
        });

      channelRef.current = channel;
    };

    subscribe();

    return () => {
      cleanup();
    };
  }, [businessId, dateStr, handleBookingChange, cleanup]);

  return {
    data,
    isLoading,
    isValidating,
    mutate: mutateBookings,
    connectionStatus,
  };
}

export function useScheduleRealtimeSubscription(
  businessId: string | null,
  date: Date | null,
  staffId?: string | null
) {
  const dateStr = date
    ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
    : null;

  const cacheKey = businessId && dateStr
    ? swrKeys.bookingsForDate(businessId, dateStr, staffId)
    : null;

  const channelRef = useRef<ReturnType<ReturnType<typeof getBrowserClient>['channel']> | null>(null);

  useEffect(() => {
    if (!businessId || !dateStr || !cacheKey) {
      return;
    }

    const supabase = getBrowserClient();
    const channelName = `schedule-sub-${businessId}-${dateStr}`;

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'bookings',
          filter: `business_id=eq.${businessId}`,
        },
        (payload) => {
          const newRecord = payload.new as BookingChangePayload | null;
          const oldRecord = payload.old as BookingChangePayload | null;

          const affectsCurrentDate = (record: BookingChangePayload | null) => {
            if (!record?.start_at) return false;
            const bookingDate = new Date(record.start_at);
            const bookingDateStr = `${bookingDate.getFullYear()}-${String(bookingDate.getMonth() + 1).padStart(2, '0')}-${String(bookingDate.getDate()).padStart(2, '0')}`;
            return bookingDateStr === dateStr;
          };

          if (affectsCurrentDate(newRecord) || affectsCurrentDate(oldRecord)) {
            mutate(cacheKey, undefined, { revalidate: true });
          }
        }
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
      channelRef.current = null;
    };
  }, [businessId, dateStr, cacheKey]);
}

export function useHomeRealtime(
  businessId: string | null,
  upcomingLimit: number = 5,
  dueLimit: number = 5
) {
  const {
    data: upcomingData,
    isLoading: upcomingLoading,
    mutate: mutateUpcoming
  } = useUpcomingBookings(businessId, upcomingLimit);

  const {
    data: dueData,
    isLoading: dueLoading,
    mutate: mutateDue
  } = useDueBookings(businessId, dueLimit);

  const channelRef = useRef<RealtimeChannel | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef(0);
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected' | 'reconnecting'>('connecting');

  const handleBookingChange = useCallback(
    (payload: RealtimePostgresChangesPayload<BookingChangePayload>) => {
      if (!businessId) return;

      const eventType = payload.eventType;
      const newRecord = payload.new as BookingChangePayload | null;
      const oldRecord = payload.old as BookingChangePayload | null;

      switch (eventType) {
        case 'INSERT': {
          if (newRecord && ['pending', 'confirmed', 'in_queue', 'in_progress'].includes(newRecord.status)) {
            mutateUpcoming(undefined, { revalidate: true });
          }
          break;
        }

        case 'UPDATE': {
          if (newRecord) {
            mutateUpcoming(
              (current) => {
                if (!current) return current;
                const isWaitingStatus = ['pending', 'confirmed', 'in_queue', 'in_progress'].includes(newRecord.status);

                if (!isWaitingStatus) {
                  return {
                    ...current,
                    bookings: current.bookings.filter((b: any) => b.id !== newRecord.id),
                    totalCount: Math.max(0, current.totalCount - 1),
                  };
                }

                const existingIndex = current.bookings.findIndex((b: any) => b.id === newRecord.id);
                if (existingIndex >= 0) {
                  const updated = [...current.bookings];
                  updated[existingIndex] = { ...updated[existingIndex], ...newRecord } as BookingWithDetails;
                  return { ...current, bookings: updated };
                }

                return current;
              },
              { revalidate: false }
            );

            mutateDue(
              (current) => {
                if (!current) return current;
                if (!['pending', 'confirmed'].includes(newRecord.status)) {
                  return {
                    ...current,
                    bookings: current.bookings.filter((b: any) => b.id !== newRecord.id),
                    totalCount: Math.max(0, current.totalCount - 1),
                  };
                }
                return current;
              },
              { revalidate: false }
            );
          }
          break;
        }

        case 'DELETE': {
          const deletedId = oldRecord?.id;
          if (deletedId) {
            mutateUpcoming(
              (current) => {
                if (!current) return current;
                return {
                  ...current,
                  bookings: current.bookings.filter((b: any) => b.id !== deletedId),
                  totalCount: Math.max(0, current.totalCount - 1),
                };
              },
              { revalidate: false }
            );
            mutateDue(
              (current) => {
                if (!current) return current;
                return {
                  ...current,
                  bookings: current.bookings.filter((b: any) => b.id !== deletedId),
                  totalCount: Math.max(0, current.totalCount - 1),
                };
              },
              { revalidate: false }
            );
          }
          break;
        }
      }
    },
    [businessId, mutateUpcoming, mutateDue]
  );

  const cleanup = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (channelRef.current) {
      const supabase = getBrowserClient();
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!businessId) {
      return;
    }

    const supabase = getBrowserClient();
    const channelName = `home-${businessId}`;

    const subscribe = () => {
      setConnectionStatus(reconnectAttempts.current > 0 ? 'reconnecting' : 'connecting');

      const channel = supabase
        .channel(channelName)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'bookings',
            filter: `business_id=eq.${businessId}`,
          },
          handleBookingChange
        )
        .subscribe((status) => {
          if (status === 'SUBSCRIBED') {
            setConnectionStatus('connected');
            reconnectAttempts.current = 0;
          } else if (status === 'CLOSED' || status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            setConnectionStatus('disconnected');

            if (reconnectAttempts.current < MAX_RECONNECT_ATTEMPTS) {
              const delay = BASE_RECONNECT_DELAY * Math.pow(2, reconnectAttempts.current);

              reconnectTimeoutRef.current = setTimeout(() => {
                reconnectAttempts.current++;
                cleanup();
                subscribe();
              }, delay);
            }
          }
        });

      channelRef.current = channel;
    };

    subscribe();

    return () => {
      cleanup();
    };
  }, [businessId, handleBookingChange, cleanup]);

  return {
    upcoming: upcomingData,
    due: dueData,
    isLoading: upcomingLoading || dueLoading,
    mutateUpcoming,
    mutateDue,
    connectionStatus,
  };
}
